import { apiRequest } from './client.js';

/** Branding requests raised against a dealer/retailer; each line forwards on its own. */

export interface Dealer {
  readonly customerId: string;
  readonly dealerCode: string;
  readonly companyName: string;
  readonly dealerFirmName: string | null;
  readonly dealerType: string;
  readonly address: string | null;
  readonly city: string | null;
  readonly districtCode: string;
  readonly stateCode: string | null;
  readonly territoryCode: string | null;
  readonly pinCode: string | null;
  readonly contactPerson: string | null;
  readonly mobileNumber: string | null;
}

export interface BrandingCatalogue {
  readonly types: ReadonlyArray<{
    brandingTypeId: string; typeCode: string; typeName: string; measurementUnit: string | null;
  }>;
  readonly items: ReadonlyArray<{
    brandingItemId: string; brandingTypeId: string; itemCode: string; itemName: string;
    /** The unit the sub-type is measured in — this decides what the form asks. */
    uomId: string | null; uomCode: string | null; uomName: string | null;
    /** True where the work is done on the dealer's own property. */
    dealerReferenceRequired: boolean;
  }>;
  readonly uoms: ReadonlyArray<{ uomId: string; uomCode: string; uomName: string }>;
  readonly cycles: ReadonlyArray<{ planningCycleId: string; cycleCode: string; cycleName: string; isCurrent: boolean }>;
}

export interface RequestLine {
  readonly requestLineId: string;
  readonly lineNumber: number;
  readonly typeName: string;
  readonly itemName: string | null;
  readonly uomCode: string;
  readonly quantity: string;
  readonly dim1: string;
  readonly dim2: string;
  readonly dim3: string | null;
  readonly dim3UomCode: string | null;
  readonly siteLocation: string | null;
  readonly serviceFromDate: string | null;
  readonly serviceToDate: string | null;
  readonly lineStatusCode: string;
  /** Whether Forward may take this line: a draft, or one whose earlier
   *  forward never started an approval. Decided by the server. */
  readonly forwardable: boolean;
  readonly forwardedAt: string | null;
  readonly remarks: string | null;
  /**
   * Who the line is waiting on right now, in readable words — null while it
   * is nobody's.
   *
   * Read live from the approval chain each time, not stamped on the line.
   * Which level a record goes to is the tenant's own configuration, so this
   * follows it from level to level instead of naming whoever it started with.
   */
  readonly pendingWith: string | null;
}

export interface HistoryRow {
  readonly requestLineId: string | null;
  readonly fromStatus: string | null;
  readonly toStatus: string;
  readonly remarks: string | null;
  readonly changedAt: string;
  readonly changedByName: string | null;
}

export interface BrandingRequestDetail {
  readonly requestId: string;
  readonly requestNo: string;
  readonly fyLabel: string;
  readonly companyName: string;
  readonly dealerCode: string;
  readonly address: string | null;
  readonly city: string | null;
  readonly districtCode: string;
  readonly stateCode: string | null;
  readonly requestDate: string;
  readonly lines: readonly RequestLine[];
  readonly history: readonly HistoryRow[];
}

export interface RequestSummary {
  readonly requestId: string;
  readonly requestNo: string;
  readonly fyLabel: string;
  readonly companyName: string;
  readonly dealerCode: string;
  readonly city: string | null;
  readonly districtCode: string;
  readonly requestDate: string;
  readonly lineCount: string;
  readonly draftCount: string;
  readonly forwardedCount: string;
}

export interface InboxRow {
  readonly requestLineId: string;
  readonly requestNo: string;
  readonly companyName: string;
  readonly city: string | null;
  readonly districtCode: string;
  readonly typeName: string;
  readonly itemName: string | null;
  readonly quantity: string;
  readonly uomCode: string;
  readonly lineStatusCode: string;
  readonly forwardedAt: string | null;
}

export interface Requesters {
  readonly departments: ReadonlyArray<{
    deptId: string; deptCode: string; deptName: string;
  }>;
  readonly members: ReadonlyArray<{
    deptId: string; userId: string; employeeCode: string;
    displayName: string; designation: string | null;
    /**
     * Whether this person can issue a work order. Someone who can is expected
     * to name one when placing an order; someone who cannot raises a request
     * and the branding team attaches the work order in review.
     */
    issuesWorkOrders: boolean;
  }>;
}

export interface NewRequestInput {
  /**
   * Only when the work is on a dealer's own property. Left out otherwise, and
   * the server files the request against the tenant's placeholder dealer.
   */
  readonly customerId?: string;
  readonly planningCycleId?: string;
  readonly remarks?: string;
  readonly requestingDeptId?: string;
  readonly requestingMemberId?: string;
  readonly lines: ReadonlyArray<{
    brandingTypeId: string;
    /** Required: the bill and the rate card are both read at sub-type level. */
    brandingItemId: string;
    uomId?: string;
    uomCode: string;
    quantity: number;
    dim1?: number;
    dim2?: number;
    dim3?: number;
    dim3UomCode?: string;
    siteLocation?: string;
    serviceFromDate?: string;
    serviceToDate?: string;
    /** The approved work order line this order draws on. */
    workOrderId?: string;
    workOrderLineId?: string;
    remarks?: string;
  }>;
}

type Tok = string | null | undefined;
const BASE = '/v1/m17/branding-requests';

export const listDealers = (token?: Tok) =>
  apiRequest<readonly Dealer[]>(`${BASE}/dealers`, { token });

export const loadBrandingCatalogue = (token?: Tok) =>
  apiRequest<BrandingCatalogue>(`${BASE}/catalogue`, { token });

/** Departments and their people, for the "raised by" pair of pickers. */
export const loadRequesters = (token?: Tok) =>
  apiRequest<Requesters>(`${BASE}/requesters`, { token });

export const listBrandingRequests = (token?: Tok, mine = false) =>
  apiRequest<{ data: readonly RequestSummary[]; total: number }>(
    `${BASE}${mine ? '?mine=true' : ''}`,
    { token },
  );

export const getBrandingRequest = (id: string, token?: Tok) =>
  apiRequest<BrandingRequestDetail>(`${BASE}/${encodeURIComponent(id)}`, { token });

export const createBrandingRequest = (body: NewRequestInput, token?: Tok) =>
  apiRequest<BrandingRequestDetail>(BASE, { method: 'POST', token, body });

export const forwardRequestLines = (lineIds: readonly string[], token?: Tok) =>
  apiRequest<BrandingRequestDetail & { forwarded: number; skippedLineNumbers: string[] }>(
    `${BASE}/lines/forward`,
    { method: 'POST', token, body: { lineIds } },
  );

export const listBrandingInbox = (token?: Tok) =>
  apiRequest<readonly InboxRow[]>(`${BASE}/inbox`, { token });
